import axios from "axios";
import { motion } from "framer-motion";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { IoIosArrowBack } from "react-icons/io";
import LoaderSpiner from "../../Components/LoaderSpiner";
import styles from "./AgentCallHistory.module.css";

const S3_BASE_URL = "https://toxbox-images.s3.ap-south-1.amazonaws.com";

const AgentCallHistory = ({ agentId, onBack }) => {
  const [callGroups, setCallGroups] = useState([]);
  const [uniqueNumbers, setUniqueNumbers] = useState([]);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedTab, setSelectedTab] = useState("info");
  const [fileContents, setFileContents] = useState({});
  const token = localStorage.getItem("token");

  // Helper: Parse transcript text
  const parseTranscript = (text) => {
    if (!text || typeof text !== "string") return [];
    const lines = text.split("\n").filter((line) => line.trim());
    return lines.map((line) => {
      const match = line.match(/^(\w+):\s*(.*)$/);
      return match
        ? { speaker: match[1].trim(), text: match[2].trim() }
        : { speaker: "Unknown", text: line.trim() };
    });
  };

  // Helper: Extract callId from key
  const extractCallId = (key) => {
    const match = key.match(/\/([0-9a-f-]{36})\//);
    return match ? match[1] : "unknown";
  };

  // Fetch agent files and group by callId
  useEffect(() => {
    if (!token || !agentId) {
      setError("Missing authentication token or agent ID");
      return;
    }
    setLoading(true);
    axios
      .get(
        `https://prod.toxboxai.com/api/call-history/inbound/files/${agentId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then((response) => {
        const files = response.data.agentFiles?.[agentId] || [];
        // Extract unique numbers
        const numbers = new Set();
        files.forEach((file) => {
          //   if (file.fromNumber) numbers.add(file.fromNumber);
          if (file.toNumber) numbers.add(file.toNumber);
        });
        setUniqueNumbers([...numbers]);

        // Group files by callId
        const grouped = {};
        files.forEach((file) => {
          const callId = extractCallId(file.key);
          if (!grouped[callId]) grouped[callId] = [];
          grouped[callId].push(file);
        });
        // For each callId, find specific file types
        const callGroupsArr = Object.entries(grouped).map(([callId, files]) => {
          files.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
          const statusFile = files.find((f) =>
            f.key.endsWith("call_status.json")
          );
          const leadAnalysisFile = files.find((f) =>
            f.key.endsWith("lead_analysis.json")
          );
          const transcriptFile = files.find((f) =>
            f.key.endsWith("transcript.txt")
          );
          const recordingFile = files.find((f) =>
            f.key.endsWith("recording.wav")
          );
          return {
            callId,
            timestamp: files[0]?.timestamp,
            statusFile,
            leadAnalysisFile,
            transcriptFile,
            recordingFile,
            fromNumber: files[0]?.fromNumber,
            toNumber: files[0]?.toNumber,
          };
        });
        setCallGroups(callGroupsArr);
        setError("");
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          localStorage.clear();
          window.location.href = "/login";
        } else {
          setError(`Failed to fetch call logs: ${err.message}`);
        }
      })
      .finally(() => setLoading(false));
  }, [agentId, token]);

  // Fetch file content from S3
  const fetchFileContent = async (file, callId, type) => {
    if (!file) return;
    try {
      const url = `${S3_BASE_URL}/${file.key}`;
      let content;
      if (type === "transcript") {
        const res = await fetch(url);
        content = parseTranscript(await res.text());
      } else {
        const res = await fetch(url);
        content = await res.json();
      }
      setFileContents((prev) => ({
        ...prev,
        [callId]: { ...prev[callId], [type]: content },
      }));
    } catch (err) {
      setError(`Failed to load ${type}: ${err.message}`);
    }
  };

  // Fetch all relevant files when a number is selected
  useEffect(() => {
    if (!selectedNumber) return;
    const calls = callGroups.filter(
      (group) =>
        group.fromNumber === selectedNumber || group.toNumber === selectedNumber
    );
    calls.forEach((group) => {
      if (group.statusFile && !fileContents[group.callId]?.statusFile) {
        fetchFileContent(group.statusFile, group.callId, "statusFile");
      }
      if (
        group.leadAnalysisFile &&
        !fileContents[group.callId]?.leadAnalysisFile
      ) {
        fetchFileContent(
          group.leadAnalysisFile,
          group.callId,
          "leadAnalysisFile"
        );
      }
      if (group.transcriptFile && !fileContents[group.callId]?.transcript) {
        fetchFileContent(group.transcriptFile, group.callId, "transcript");
      }
    });
  }, [selectedNumber, callGroups, fileContents]);

  // Handle number click
  const handleNumberClick = (number) => {
    setSelectedNumber(number);
    setSelectedTab("info");
    setFileContents({});
  };

  // Filter call groups by selected number
  const filteredCallGroups = selectedNumber
    ? callGroups.filter(
      (group) =>
        group.fromNumber === selectedNumber ||
        group.toNumber === selectedNumber
    )
    : [];

  // Render sections based on tab
  const renderSection = () => {
    if (selectedTab === "info") {
      return (
        <div>
          <h3>Call Info</h3>
          {filteredCallGroups.map((group) => (
            <div key={group.callId} className={styles.section}>
              <h4>Call ID: {group.callId}</h4>
              {fileContents[group.callId]?.statusFile ? (
                <div>
                  <strong>Call Status</strong> (
                  {new Date(group.timestamp).toLocaleString("en-US")}):
                  <div>
                    <p>
                      <strong>Created:</strong>{" "}
                      {new Date(
                        fileContents[group.callId].statusFile.created
                      ).toLocaleString("en-US")}
                    </p>
                    <p>
                      <strong>Ended:</strong>{" "}
                      {fileContents[group.callId].statusFile.ended
                        ? new Date(
                          fileContents[group.callId].statusFile.ended
                        ).toLocaleString("en-US")
                        : "N/A"}
                    </p>
                    <p>
                      <strong>Billed Duration:</strong>{" "}
                      {fileContents[group.callId].statusFile.billedDuration}
                    </p>
                    <p>
                      <strong>End Reason:</strong>{" "}
                      {fileContents[group.callId].statusFile.endReason}
                    </p>
                  </div>
                </div>
              ) : (
                <p>Loading call status...</p>
              )}
              {fileContents[group.callId]?.leadAnalysisFile ? (
                <div>
                  <strong>Lead Analysis</strong> (
                  {new Date(group.timestamp).toLocaleString("en-US")}):
                  <div>
                    <p>
                      <strong>Lead Type:</strong>{" "}
                      {fileContents[group.callId].leadAnalysisFile.leadType}
                    </p>
                    <p>
                      <strong>Summary:</strong>{" "}
                      {fileContents[group.callId].leadAnalysisFile.summary}
                    </p>
                    <p>
                      <strong>Rating:</strong>{" "}
                      {fileContents[group.callId].leadAnalysisFile.rating}
                    </p>
                  </div>
                </div>
              ) : (
                group.leadAnalysisFile && <p>Loading lead analysis...</p>
              )}
            </div>
          ))}
        </div>
      );
    } else if (selectedTab === "transcript") {
      return (
        <div>
          <h3>Transcripts</h3>
          {filteredCallGroups.map((group) => (
            <div key={group.callId} className={styles.section}>
              <h4>Call ID: {group.callId}</h4>
              {fileContents[group.callId]?.transcript ? (
                <div className={styles.transcript}>
                  {fileContents[group.callId].transcript.map((entry, idx) => (
                    <div
                      key={idx}
                      className={`${styles.message} ${entry.speaker.toLowerCase() === "agent"
                        ? styles.agentMessage
                        : styles.userMessage
                        }`}
                    >
                      <span className={styles.speaker}>{entry.speaker}:</span>
                      <span className={styles.messageText}>{entry.text}</span>
                    </div>
                  ))}
                </div>
              ) : (
                group.transcriptFile && <p>Loading transcript...</p>
              )}
            </div>
          ))}
        </div>
      );
    } else if (selectedTab === "recording") {
      return (
        <div>
          <h3>Recordings</h3>
          {filteredCallGroups.map((group) => (
            <div key={group.callId} className={styles.section}>
              <h4>Call ID: {group.callId}</h4>
              {group.recordingFile && (
                <div>
                  <strong>Recording</strong> (
                  {new Date(group.timestamp).toLocaleString("en-US")}):
                  <audio controls className={styles.audioPlayer}>
                    <source
                      src={`${S3_BASE_URL}/${group.recordingFile.key}`}
                      type="audio/wav"
                    />
                    Your browser does not support the audio element.
                  </audio>
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }
  };

  return (
    <div className={styles.container}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (selectedNumber) {
            setSelectedNumber(null); // Go back to number list
          } else {
            onBack(); // Go back to previous page
          }
        }}
        className={styles.backButton}
      >
        <IoIosArrowBack /> {selectedNumber ? "Back to Numbers" : "Back"}
      </button>
      <h2>Agent Call Logs</h2>
      {loading && <LoaderSpiner />}
      {error && <p className={styles.error}>{error}</p>}
      {!loading && !error && uniqueNumbers.length === 0 && (
        <p className={styles.noData}>No call logs found for this agent.</p>
      )}
      {!loading && !error && uniqueNumbers.length > 0 && !selectedNumber && (
        <div className={styles.numberList}>
          <h3>Phone Numbers</h3>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Phone Number</th>
                </tr>
              </thead>
              <tbody>
                {uniqueNumbers.map((number) => (
                  <tr key={number} className={styles.callRow}>
                    <td>
                      <span
                        className={styles.callIdCell}
                        onClick={() => handleNumberClick(number)}
                      >
                        {number}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {!loading && !error && selectedNumber && (
        <motion.div
          className={styles.cardContainer}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <h3>Calls for {selectedNumber}</h3>
          {filteredCallGroups.length === 0 && (
            <p className={styles.noData}>No calls found for this number.</p>
          )}
          {filteredCallGroups.length > 0 && (
            <>
              <div className={styles.tabBar}>
                <button
                  className={`${styles.tabButton} ${selectedTab === "info" ? styles.active : ""
                    }`}
                  onClick={() => setSelectedTab("info")}
                >
                  Call Info
                </button>
                <button
                  className={`${styles.tabButton} ${selectedTab === "transcript" ? styles.active : ""
                    }`}
                  onClick={() => setSelectedTab("transcript")}
                >
                  Transcripts
                </button>
                <button
                  className={`${styles.tabButton} ${selectedTab === "recording" ? styles.active : ""
                    }`}
                  onClick={() => setSelectedTab("recording")}
                >
                  Recordings
                </button>
              </div>
              {renderSection()}
            </>
          )}
        </motion.div>
      )}
    </div>
  );
};

AgentCallHistory.propTypes = {
  agentId: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default AgentCallHistory;
